import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { disableHiddenStandardControls, isStandardFieldHidden } from "../../standard-field-visibility.helper";
import { FormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ToastService } from "@core/components/toasts/toasts.service";
import { AuthService } from "services/auth.service";
import { CostCenterService } from "services/corporate/costcenter.service";
import { EmployeeService } from "services/corporate/employee.service";
import { DomainService } from "services/domain.service";
import { ApprovalScopeActionType } from "services/enums/approvalScopeActionType.enum";
import { UnhealthinessType } from "services/enums/UnhealthinessType.enum";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { JobTitleService } from "services/hiring/jobTitle.service";
import { JobTitleModel } from "services/models/hiring/jobTitle.model";
import { OptionModel } from "services/models/model";
import { QueryParams } from "services/models/odata/queryParams";
import { Filter } from "services/models/odata/filter";
import { WorkforcePlanDrawerEmployeeService } from "services/workforceplan/workforceplan-employee.service";
import { WorkforceplanService } from "services/workforceplan/workforceplan-main.service";
import { WorkforceplanMovementTypeService } from "services/workforceplan/workforceplan-movement-type.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
	DrawerDataInput,
	ISolicitationFormComponent,
	FormCostCenterPayload,
	FormCostCenterValues,
	MovementSalaryRange,
	NewRecruitmentEmployee,
} from "../../form-types";
import { normalizeSalaryValue } from "@core/utils/currency-value.helper";
import {
	buildErpApprovalFieldVisibilityParams,
	canEditErpIntegrationFields as resolveCanEditErpIntegrationFields,
	canShowExecutionDate as resolveCanShowExecutionDate,
	canShowTradeUnionField as resolveCanShowTradeUnionField,
} from "../../erp-approval-field-visibility.helper";
import { pagesActions } from "app/page-access";
import { guidEmpty } from "utils/helps";
import { WorkforcePlanTeamEmployeeModel } from "services/models/workforceplan";

type OpenPositionTarget = {
	jobTitleId: string;
	jobTitleName: string;
	jobTitleGroupId: string;
	jobTitleGroupName: string;
	costCenterId: string;
	costCenterName: string;
};

type CostCenterChangeLists = {
	currentCenterCostsOptions?: OptionModel[];
	currentJobTitleOptions?: OptionModel[];
	centerCostsOptions?: OptionModel[];
	jobTitleOptions?: OptionModel[];
	jobTitleGroupOptions?: OptionModel[];
	trainedEmployeeOptions?: NewRecruitmentEmployee[];
	movementTypeOptions?: OptionModel[];
	tradeUnionsOptions?: OptionModel[];
	jobTitles?: JobTitleModel[];
};

@Component({
	selector: "app-cost-center-change-form",
	templateUrl: "./cost-center-change-form.component.html",
	styleUrls: ["./cost-center-change-form.component.scss"],
})
export class CostCenterChangeFormComponent implements OnChanges, OnDestroy, ISolicitationFormComponent {
	@Input() data: DrawerDataInput | null = null;

	/** Origin of the drawer: 'action-required' when opened from action-required list; null otherwise */
	@Input() source: "action-required" | null = null;
	@Input() hiddenStandardFields: string[] = [];
	/** Fluxo com "Permite troca para CC diferente" = Não e usuário fora da lista de exceção: só o CC atual do colaborador. */
	@Input() canChangeToDifferentCostCenter = true;

	/** Emits true when employee is being fetched, false when done (success or error). Parent can show loading in employee block. */
	@Output() employeeLoadingChange = new EventEmitter<boolean>();

	form: UntypedFormGroup;
	lists: CostCenterChangeLists = {};
	unhealthinessOptions = UnhealthinessType.buildListEnum();

	/** Set when employee fetch fails; can be used to show error state or disable form */
	employeeLoadError = false;

	/** Loading state for job title/group fetch when cost center changes */
	isJobTitlesLoading = false;
	isTrainedEmployeeLoading = false;
	canManageEmploymentContractInformation = false;

	private openPositionTarget: OpenPositionTarget | null = null;
	private _openPositionHasJobTitle = false;
	private destroy$ = new Subject<void>();

	constructor(
		private _formBuilder: FormBuilder,
		private _costCenterService: CostCenterService,
		private _employeeService: EmployeeService,
		private _workforceplanService: WorkforceplanService,
		private _jobTitleService: JobTitleService,
		private _domainService: DomainService,
		private _movementTypeService: WorkforceplanMovementTypeService,
		private _drawerEmployeeService: WorkforcePlanDrawerEmployeeService,
		private _toastService: ToastService,
		private _authService: AuthService
	) {
		this.form = this._formBuilder.group({
			note: ["", Validators.required],
			employeeId: [null, Validators.required],
			movementType: [WorkforceplanMovementType.COST_CENTER_AND_JOB_TITLE_CHANGE.id],
			newJobTitleGroupId: [null, Validators.required],
			newJobTitleGroupName: [null, Validators.required],
			newJobTitleId: [null, Validators.required],
			workScaleGroup: [null],
			newCostCenterId: [null, Validators.required],
			workforcePlanId: [null],
			currentJobTitleId: [null],
			currentCostCenterId: [null],
			newSalary: [],
			tradeUnionId: [],
			tradeUnionName: [],
			changeTypeCode: [],
			hasDangerousness: [false],
			unhealthinessType: [],
			executionDate: [null],
			salaryRange: [null]
		});

		this.canManageEmploymentContractInformation = this._authService.hasActionAllowed(
			pagesActions.workforcePlan.canManageEmploymentContractInformation,
			"/workforceplan",
			true
		);
	}

	public isHidden(fieldKey: string): boolean {
		return isStandardFieldHidden(this.hiddenStandardFields, fieldKey);
	}

	private applyHiddenStandardFields(): void {
		disableHiddenStandardControls(this.form, this.hiddenStandardFields);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["hiddenStandardFields"] && this.form && !changes["data"]?.currentValue) {
			this.applyHiddenStandardFields();
		}
		if (
			changes["canChangeToDifferentCostCenter"] &&
			this.form &&
			!changes["data"]?.currentValue
		) {
			this._applyDifferentCostCenterRestriction();
		}

		const dataChanged = !!changes["data"]?.currentValue;
		if (!dataChanged || !this.data || this.data.type !== "COST_CENTER_AND_JOB_TITLE_CHANGE") {
			return;
		}
		this.employeeLoadError = false;

		if (this.data.mode === "details" && this.data.solicitationDetails) {
			this._buildFormForDetails();
		} else {
			this._buildFormAndLoadData();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private async _buildFormForDetails(): Promise<void> {
		await this.ensureEmployeeLoaded();

		const { solicitation, solicitationDetails } = this.data!;
		const pd = solicitationDetails?.payloadData || {};
		this._persistedTradeUnionId = pd.tradeUnionId ?? null;
		this._companyHasTradeUnions = null;

		this.form.patchValue({
			note: solicitationDetails?.note ?? "",
			employeeId: solicitation?.employeeId ?? null,
			movementType: solicitation?.movementTypeData?.id ?? WorkforceplanMovementType.COST_CENTER_AND_JOB_TITLE_CHANGE.id,
			newJobTitleGroupId: pd.newJobTitleGroupId ?? null,
			newJobTitleGroupName: pd.newJobTitleGroupName ?? null,
			newJobTitleId: pd.newJobTitleId ?? null,
			workScaleGroup: solicitation?.workScaleGroup ?? this.data?.workScaleGroup ?? null,
			newCostCenterId: pd.newCostCenterId ?? null,
			workforcePlanId: this.data?.workforcePlanId ?? null,
			currentJobTitleId: pd.currentJobTitleId ?? null,
			currentCostCenterId: pd.currentCostCenterId ?? null,
			newSalary: normalizeSalaryValue(pd.newSalary),
			tradeUnionId: pd.tradeUnionId ?? null,
			tradeUnionName: pd.tradeUnionName ?? null,
			changeTypeCode: pd.changeTypeCode ?? null,
			hasDangerousness: pd.hasDangerousness ?? false,
			unhealthinessType: UnhealthinessType.resolveValue(pd.unhealthinessType),
			executionDate: pd.executionDate ? (pd.executionDate as string).substring(0, 10) : null,
			salaryRange: pd.salaryRange ?? null,
		});

		this._patchListsFromSolicitationDetails();

		if (pd.newJobTitleGroupId && pd.newJobTitleGroupName) {
			this.lists.jobTitleGroupOptions = [
				{ label: pd.newJobTitleGroupName, value: pd.newJobTitleGroupId } as OptionModel,
			];
		}

		if (!this.data?.solicitationDetails?.permission?.canUpdate) {
			this.form.disable();
		}

		if (this.canShowFieldsToChangeJobTitleOrCostCenter) {
			this._loadTradeUnionsOptions();
		}

		if (this.canShowFieldsToChangeJobTitleOrCostCenter) {
			this._loadMovementTypes();
		}

		this.form.get("hasDangerousness")?.disable();
		this.form.get("unhealthinessType")?.disable();

		this.form.get("salaryRange")?.disable();

		if (this.canEditFieldToChangeJobTitleTrained) {
			this.form.get("salaryRange")?.enable();
			const newSalaryCtrl = this.form.get("newSalary");
			const tradeUnionIdCtrl = this.form.get("tradeUnionId");
			const changeTypeCodeCtrl = this.form.get("changeTypeCode");
			const hasDangerousnessCtrl = this.form.get("hasDangerousness");
			const unhealthinessTypeCtrl = this.form.get("unhealthinessType");
			const executionDateCtrl = this.form.get("executionDate");

			newSalaryCtrl?.enable();
			newSalaryCtrl?.setValidators([Validators.required]);
			tradeUnionIdCtrl?.enable();
			tradeUnionIdCtrl?.setValidators([Validators.required]);
			changeTypeCodeCtrl?.enable();
			changeTypeCodeCtrl?.setValidators([Validators.required]);
			hasDangerousnessCtrl?.enable();
			hasDangerousnessCtrl?.setValidators([Validators.required]);
			if (hasDangerousnessCtrl?.value == null) {
				hasDangerousnessCtrl?.setValue(false);
			}
			if (!this.isUnhealthinessFilledByDedicatedStep) {
				unhealthinessTypeCtrl?.enable();
				unhealthinessTypeCtrl?.setValidators([Validators.required]);
			}
			executionDateCtrl?.enable();
			executionDateCtrl?.setValidators([Validators.required]);
			this.form.updateValueAndValidity();

			if (!this.data?.companyId) {
				this._applyEmployeeTradeUnionDefault();
			}

			if (pd.salaryRange && newSalaryCtrl?.value == null) {
				newSalaryCtrl?.setValue(pd.salaryRange.amount);
			}
		}
		else if (this.canEditFieldToApproveUnhealthiness) {
			const unhealthinessTypeCtrl = this.form.get("unhealthinessType");
			const hasDangerousnessCtrl = this.form.get("hasDangerousness");
			unhealthinessTypeCtrl?.enable();
			unhealthinessTypeCtrl?.setValidators([Validators.required]);
			hasDangerousnessCtrl?.enable();
			if (hasDangerousnessCtrl?.value == null) {
				hasDangerousnessCtrl?.setValue(false);
			}
			this.form.updateValueAndValidity();
		}
		else if (this.canEditErpUnhealthinessFields) {
			const hasDangerousnessCtrl = this.form.get("hasDangerousness");
			const unhealthinessTypeCtrl = this.form.get("unhealthinessType");

			hasDangerousnessCtrl?.enable();
			hasDangerousnessCtrl?.setValidators([Validators.required]);
			if (!this.isUnhealthinessFilledByDedicatedStep) {
				unhealthinessTypeCtrl?.enable();
				unhealthinessTypeCtrl?.setValidators([Validators.required]);
			}
			this.form.updateValueAndValidity();
		}
		this.applyHiddenStandardFields();
	}

	private async _buildFormAndLoadData(): Promise<void> {
		this._persistedTradeUnionId = null;
		this._companyHasTradeUnions = null;
		await this.ensureEmployeeLoaded();

		this.form.patchValue({
			newJobTitleGroupId: this.data?.employee.jobTitleGroupId ?? null,
			newJobTitleGroupName: this.data?.employee.jobTitleGroupName ?? null,
			workforcePlanId: this.data?.workforcePlanId,
			workScaleGroup: this.data?.workScaleGroup,
		});

		if (this.data?.hasSubType) {
			this._captureOpenPositionTarget();
			await this._resolveOrganicCostCenterLabel();
			await this._loadTrainedEmployeesInJobTitleGroupId();
			this._applyNewScenarioFromOpenPosition();
		} else {
			await this._resolveOrganicCostCenterLabel();
			const currentCostCenterId =
				this.data!.employee.centerCostId || this.data?.costCenterId || null;
			const currentCostCenterName =
				this.data!.employee.costCenterName || this.data?.costCenterName || null;

			this.form.patchValue({
				employeeId: this.data!.employee.id,
				currentJobTitleId: this.data!.employee.jobTitleId,
				currentCostCenterId,
				newCostCenterId: currentCostCenterId,
			});

			this.lists.currentCenterCostsOptions = currentCostCenterId
				? [{ label: currentCostCenterName || currentCostCenterId, value: currentCostCenterId }]
				: [];

			if (currentCostCenterId) {
				this.lists.centerCostsOptions = [
					{ label: currentCostCenterName || currentCostCenterId, value: currentCostCenterId },
				];
				this._listJobTitleGroupsFromWorkforcePlan(currentCostCenterId, this.data!.employee.companyId);
				this._listJobTitlesFromEffectiveWorkforcePlan(currentCostCenterId, this.data!.employee.companyId);
			}

			this.lists.currentJobTitleOptions = [
				{
					label: this.data!.employee.jobTitleName,
					value: this.data!.employee.jobTitleId,
				},
			];
		}

		this._applyDifferentCostCenterRestriction();
		this.applyHiddenStandardFields();
	}

	public get isNewCostCenterLocked(): boolean {
		return !this.canChangeToDifferentCostCenter && this.data?.mode === "add" && !this.data?.hasSubType;
	}

	private _applyDifferentCostCenterRestriction(): void {
		if (!this.form || !this.data || this.data.mode !== "add" || this.data.hasSubType || !this.data.employee) {
			return;
		}

		const newCostCenterControl = this.form.get("newCostCenterId");

		if (this.canChangeToDifferentCostCenter) {
			newCostCenterControl?.enable({ emitEvent: false });
			this._loadCostCentersOptions();
			this.applyHiddenStandardFields();
			return;
		}

		const currentCostCenterId =
			this.form.get("currentCostCenterId")?.value || this.data.employee.centerCostId || this.data.costCenterId || null;
		if (!currentCostCenterId) {
			newCostCenterControl?.disable({ emitEvent: false });
			this.applyHiddenStandardFields();
			return;
		}

		const currentCostCenterName =
			this.data.employee.costCenterName || this.data.costCenterName || currentCostCenterId;
		this.lists.centerCostsOptions = [{ label: currentCostCenterName, value: currentCostCenterId }];

		if (newCostCenterControl?.value !== currentCostCenterId) {
			this.form.patchValue({ newCostCenterId: currentCostCenterId });
			this._listJobTitleGroupsFromWorkforcePlan(currentCostCenterId, this.data.employee.companyId);
			this._listJobTitlesFromEffectiveWorkforcePlan(currentCostCenterId, this.data.employee.companyId);
		}

		newCostCenterControl?.disable({ emitEvent: false });
		this.applyHiddenStandardFields();
	}

	getForm(): UntypedFormGroup {
		return this.form;
	}

	getPayload(): FormCostCenterPayload {
		const v = this.form.getRawValue() as FormCostCenterValues;
		const currentCostCenterId =
			v.currentCostCenterId || this.data?.employee?.centerCostId || this.data?.costCenterId;
		const currentCostCenterName =
			this.data?.employee?.costCenterName || this.data?.costCenterName;
		const currentJobTitleName = this.data?.employee?.jobTitleName;
		const newCostCenterId = v.newCostCenterId || currentCostCenterId;
		const jobTitleInfos = v.newJobTitleId
			? {
				currentJobTitleId: v.currentJobTitleId,
				currentJobTitleName,
				newJobTitleId: v.newJobTitleId,
				newJobTitleName: this._getJobTitleOption(v.newJobTitleId),
			}
			: {};
		const costCenterInfos =
			currentCostCenterId || newCostCenterId
				? {
					currentCostCenterId,
					currentCostCenterName,
					newCostCenterId,
					newCostCenterName: newCostCenterId
						? this._getCostCenterTitleOption(newCostCenterId)
						: null,
				}
				: {};
		return {
			note: v.note,
			employeeId: v.employeeId,
			movementType: v.movementType,
			workScaleGroup: v.workScaleGroup,
			workforcePlanId: v.workforcePlanId,
			payload: JSON.stringify({
				...jobTitleInfos,
				...costCenterInfos,
				newJobTitleGroupId: v.newJobTitleGroupId,
				newJobTitleGroupName: v.newJobTitleGroupName,
				changeTypeCode: v.changeTypeCode,
				hasDangerousness: v.hasDangerousness,
				unhealthinessType: v.unhealthinessType,
				newSalary: normalizeSalaryValue(v.newSalary),
				...this._tradeUnionPayload(v.tradeUnionId),
				executionDate: v.executionDate,
				salaryRange: v.salaryRange ?? null,
			}),
		} as FormCostCenterPayload;
	}

	getUpdatePayload(): string {
		const v = this.form.getRawValue() as FormCostCenterValues;
		return JSON.stringify({
			changeTypeCode: v.changeTypeCode,
			hasDangerousness: v.hasDangerousness,
			unhealthinessType: v.unhealthinessType,
			newSalary: normalizeSalaryValue(v.newSalary),
			...this._tradeUnionPayload(v.tradeUnionId),
			executionDate: v.executionDate,
			salaryRange: this.form.getRawValue().salaryRange ?? null,
		});
	}

	/** Solicitação criada antes da faixa existir não fica bloqueada na homologação. */
	get isSalaryRangeRequired(): boolean {
		return this.data?.mode !== "details" || !!this.data?.solicitationDetails?.payloadData?.salaryRange;
	}

	/** Com faixa salarial, o salário é o valor da faixa e o campo fica oculto. */
	get hasSalaryRange(): boolean {
		return !!this.form.get("salaryRange")?.value;
	}

	onSalaryRangeSelected(range: MovementSalaryRange | null): void {
		if (range && this.canEditFieldToChangeJobTitleTrained) {
			this.form.get("newSalary")?.setValue(range.amount);
		}
	}

	get canShowFieldsToChangeJobTitleOrCostCenter(): boolean {
		if (this.canEditFieldToChangeJobTitleTrained) {
			return true;
		}
		const payload = this.data?.solicitationDetails?.payloadData;
		return (
			!!(
				payload?.newSalary ||
				payload?.changeTypeCode
			) &&
			this.canManageEmploymentContractInformation
		);
	}

	private get canEditErpIntegrationFields(): boolean {
		return resolveCanEditErpIntegrationFields(this._erpFieldVisibilityParams);
	}

	get canShowExecutionDate(): boolean {
		return resolveCanShowExecutionDate(this._erpFieldVisibilityParams);
	}

	/** null enquanto não carregou; false quando a empresa não tem sindicato cadastrado. */
	private _companyHasTradeUnions: boolean | null = null;
	/** Sindicato já gravado na solicitação. O id do colaborador não entra nessa exceção. */
	private _persistedTradeUnionId: string | null = null;

	get canShowTradeUnionField(): boolean {
		// Empresa sem sindicato não exige sindicato (mesma regra da API); só mostra se a solicitação já tiver um.
		if (this._companyHasTradeUnions === false && !this._persistedTradeUnionId) return false;
		return resolveCanShowTradeUnionField(this._erpFieldVisibilityParams);
	}

	private get _erpFieldVisibilityParams() {
		return buildErpApprovalFieldVisibilityParams(
			this.data?.solicitationDetails,
			this.canExecute,
			this.canManageEmploymentContractInformation,
			this.canEditFieldToChangeJobTitleTrained
		);
	}

	private get canEditFieldToChangeJobTitleTrained(): boolean {
		return (
			this.data?.solicitationDetails?.hasStepActionType(
				ApprovalScopeActionType.SHOW_FIELDS_TO_APPROVE_NEW_JOBTITLE_TRAINED.value
			) &&
			this.canExecute
		);
	}

	private get isUnhealthinessFilledByDedicatedStep(): boolean {
		return !!this.data?.solicitationDetails?.unhealthinessFilledByDedicatedStep;
	}

	private get canEditFieldToApproveUnhealthiness(): boolean {
		return (
			this.data?.solicitationDetails?.hasStepActionType(
				ApprovalScopeActionType.SHOW_FIELD_TO_APPROVE_UNHEALTHINESS.value
			) &&
			this.canExecute
		);
	}

	private get canEditErpUnhealthinessFields(): boolean {
		return !!(
			this.data?.solicitationDetails?.requiresChangeEmployeeParamsForErp() &&
			this.canExecute
		);
	}

	get isNewJobTitleDisabled(): boolean {
		return !!(this.data?.hasSubType && this._openPositionHasJobTitle);
	}

	private get canExecute(): boolean {
		return (
			!!(this.data?.solicitation?.status === "PENDING" &&
				this.data?.solicitationDetails?.permission?.canExecute)
		);
	}

	getSelectedOptionText(options: OptionModel[] | unknown[] | undefined, value: unknown): string | null {
		if (!options || value == null || options.length === 0) return null;
		const option = (options as OptionModel[]).find(
			(opt) => opt.value === value || opt.label === value
		);
		return option?.label ?? null;
	}

	onCenterCostChanged(data: OptionModel | null): void {
		const field = this.form.get("newCostCenterId");
		field?.setValidators(Validators.required);
		if (data) {
			this.isJobTitlesLoading = true;
			field?.setValue(data.value);
			this._listJobTitleGroupsFromWorkforcePlan(data.value, this.data!.employee.companyId);
			this._listJobTitlesFromEffectiveWorkforcePlan(data.value, this.data!.employee.companyId);
		} else {
			this.isJobTitlesLoading = false;
			field?.setValue(null);
			this.lists.jobTitleOptions = [];
			this.lists.jobTitleGroupOptions = [];
		}
	}

	onJobTitleChange(data: OptionModel | null): void {
		const field = this.form.get("newJobTitleId");
		field?.setValidators(Validators.required);
		if (!data) return;
		// A mesma função só é permitida quando a troca leva o colaborador para outro centro de custo.
		const newCostCenterId = this.form.get("newCostCenterId")?.value;
		const keepsCostCenter = !newCostCenterId || newCostCenterId === this.form.get("currentCostCenterId")?.value;
		if (this.form.get("currentJobTitleId")?.value === data.value && keepsCostCenter) {
			this._toastService.showWarning(
				"A nova função é igual a função atual, selecione uma função diferente ou outro centro de custo."
			);
			field?.setValue(null);
			return;
		}
		field?.setValue(data.value);
		if (!this.data?.hasSubType) {
			const jobTitle = this.lists.jobTitles?.find((f) => f.id === data.value);
			if (jobTitle?.jobTitleGroupId) {
				const jobTitleGroup = this.lists.jobTitleGroupOptions?.find(
					(f) => f.value === jobTitle.jobTitleGroupId
				);
				this.form.patchValue({
					newJobTitleGroupId: jobTitleGroup?.value ?? jobTitle.jobTitleGroupId,
					newJobTitleGroupName: jobTitleGroup?.label ?? null,
				});
				if (!jobTitleGroup) {
					this._toastService.showWarning("Grupo de cargo não encontrado para a função selecionada.");
				}
			} else {
				this._toastService.showWarning(
					"Não foi possível identificar o grupo de cargo para esta função."
				);
			}
		}
	}

	async onTrainedEmployeeChanged(employeeId: string | NewRecruitmentEmployee): Promise<void> {
		if (!this.data) return;

		const selectedId = typeof employeeId === "string" ? employeeId : employeeId?.id;
		const trainedEmployee = this.lists.trainedEmployeeOptions?.find((e) => e.id === selectedId);
		if (!selectedId || !trainedEmployee) return;

		this.data.employee = { ...this.data.employee, id: selectedId } as WorkforcePlanTeamEmployeeModel;
		await this._fetchAndMergeEmployeeData();

		this.lists.currentCenterCostsOptions = [
			{
				label: this.data.employee.costCenterName,
				value: this.data.employee.centerCostId,
			},
		];
		this.lists.currentJobTitleOptions = [
			{
				label: this.data.employee.jobTitleName,
				value: this.data.employee.jobTitleId,
			},
		];

		this.form.patchValue({
			employeeId: selectedId,
			currentJobTitleId: this.data.employee.jobTitleId,
			currentCostCenterId: this.data.employee.centerCostId,
		});

		this._applyOpenPositionCostCenterAndCargo();

		if (this._openPositionHasJobTitle) {
			return;
		}

		if (trainedEmployee.jobTitleId && trainedEmployee.jobTitleId !== guidEmpty) {
			this.form.patchValue({
				newJobTitleId: trainedEmployee.jobTitleId,
				newJobTitleGroupId: trainedEmployee.jobTitleGroupId ?? this.form.value.newJobTitleGroupId,
				newJobTitleGroupName: trainedEmployee.jobTitleGroupName ?? this.form.value.newJobTitleGroupName,
			});
		} else {
			this.lists.jobTitleOptions = this._buildTrainedJobTitleOptions(
				this.lists.trainedEmployeeOptions ?? []
			);
		}
	}

	private _patchListsFromSolicitationDetails(): void {
		const sd = this.data?.solicitationDetails?.payloadData;
		if (!sd) return;
		this.lists.currentCenterCostsOptions = [
			{ label: sd.currentCostCenterName, value: sd.currentCostCenterId },
		];
		this.lists.centerCostsOptions = [
			{ label: sd.newCostCenterName, value: sd.newCostCenterId },
		];
		this.lists.currentJobTitleOptions = [
			{ label: sd.currentJobTitleName, value: sd.currentJobTitleId },
		];
		this.lists.jobTitleOptions = [
			{ label: sd.newJobTitleName, value: sd.newJobTitleId },
		];
	}

	private _captureOpenPositionTarget(): void {
		const employee = this.data?.employee;
		if (!employee) return;

		this._openPositionHasJobTitle = !!(employee.jobTitleId && employee.jobTitleId !== guidEmpty);

		this.openPositionTarget = {
			jobTitleId: employee.jobTitleId,
			jobTitleName: employee.jobTitleName,
			jobTitleGroupId: employee.jobTitleGroupId,
			jobTitleGroupName: employee.jobTitleGroupName,
			costCenterId: this.data?.costCenterId ?? "",
			costCenterName: this.data?.costCenterName ?? "",
		};
	}

	private _applyOpenPositionCostCenterAndCargo(): void {
		if (!this.openPositionTarget) return;

		const target = this.openPositionTarget;
		if (!target.costCenterId && this.data?.costCenterId) {
			target.costCenterId = this.data.costCenterId;
		}
		if (!target.costCenterId) return;

		if (target.jobTitleGroupId) {
			this.lists.jobTitleGroupOptions = [
				{ label: target.jobTitleGroupName, value: target.jobTitleGroupId },
			];
		}

		this.lists.centerCostsOptions = [
			{ label: target.costCenterName || target.costCenterId, value: target.costCenterId },
		];

		const patch: Record<string, unknown> = {
			newCostCenterId: target.costCenterId,
			newJobTitleGroupId: target.jobTitleGroupId ?? null,
			newJobTitleGroupName: target.jobTitleGroupName ?? null,
		};

		if (this._openPositionHasJobTitle && target.jobTitleId && target.jobTitleId !== guidEmpty) {
			this.lists.jobTitleOptions = [
				{ label: target.jobTitleName, value: target.jobTitleId },
			];
			patch.newJobTitleId = target.jobTitleId;
		}

		this.form.patchValue(patch);
	}

	private _applyNewScenarioFromOpenPosition(): void {
		this._applyOpenPositionCostCenterAndCargo();

		if (!this._openPositionHasJobTitle && this.data?.hasSubType) {
			this.lists.jobTitleOptions = this._buildTrainedJobTitleOptions(
				this.lists.trainedEmployeeOptions ?? []
			);
		}
	}

	private _filterTrainedEmployeesForOpenPosition(
		employees: NewRecruitmentEmployee[]
	): NewRecruitmentEmployee[] {
		const ccId = this.openPositionTarget?.costCenterId || this.data?.costCenterId;
		const groupId = this.openPositionTarget?.jobTitleGroupId || this.data?.employee?.jobTitleGroupId;
		const replacedId = this.data?.replacedEmployeeId?.toLowerCase();
		const candidates = employees.filter(
			(e) => !replacedId || e.id?.toLowerCase() !== replacedId
		);
		if (!ccId || !groupId) return candidates;

		return candidates.filter(
			(e) => e.costCenterId === ccId && e.jobTitleGroupId === groupId
		);
	}

	private _buildTrainedJobTitleOptions(employees: NewRecruitmentEmployee[]): OptionModel[] {
		const seen = new Set<string>();
		return employees
			.filter((e) => e.jobTitleId && e.jobTitleId !== guidEmpty)
			.filter((e) => {
				if (seen.has(e.jobTitleId)) return false;
				seen.add(e.jobTitleId);
				return true;
			})
			.map((e) => ({ label: e.jobTitleName, value: e.jobTitleId } as OptionModel));
	}

	private async _resolveOrganicCostCenterLabel(): Promise<void> {
		if (!this.data?.costCenterId) return;

		try {
			const response = await this._costCenterService
				.listByHierarchyAndCompany(this.data.companyId)
				.pipe(takeUntil(this.destroy$))
				.toPromise();
			const match = response?.data?.find((cc) => cc.id === this.data!.costCenterId);
			if (match) {
				this.data.costCenterName = match.name;
				if (this.openPositionTarget) {
					this.openPositionTarget.costCenterName = match.name;
				}
			}
		} catch {
			// label optional for readonly display
		}
	}

	private _isEmployeePartial(): boolean {
		const emp = this.data?.employee;
		if (!emp || !emp.id || emp.id === guidEmpty) return false;
		return !emp.jobTitleId || !emp.centerCostId || !emp.jobTitleGroupId;
	}

	private async ensureEmployeeLoaded(): Promise<void> {
		if (!this.data?.employee || !this._isEmployeePartial()) return;

		await this._fetchAndMergeEmployeeData();

		if (!this.data?.companyId && this.data?.employee?.companyId) {
			this.data.companyId = this.data.employee.companyId;
		}
	}

	/**
	 * Fetches employee by id and merges only the fields used by this component into data.employee.
	 * API costCenterId is mapped to centerCostId for drawer model consistency.
	 */
	private async _fetchAndMergeEmployeeData(): Promise<void> {
		if (!this.data?.employee || this.data.employee.id === guidEmpty) return;
		this.employeeLoadError = false;
		this.employeeLoadingChange.emit(true);
		try {
			const params = [
				'$select=id,name,registration,profileImagePath,companyId,jobTitleId,jobTitleName,costCenterId,costCenterName,tradeUnionId',
				`$filter=id eq ${this.data.employee.id}`
			];
			const [empResponse] = await this._employeeService.getListFiltered(params).toPromise();
			const existing = this.data!.employee;
			this.data!.employee = {
				...existing,
				id: empResponse?.id ?? existing.id,
				name: empResponse?.name ?? existing.name,
				registration: empResponse?.registration ?? existing.registration,
				profileImagePath: empResponse?.shortProfileImagePath ?? existing.profileImagePath,
				companyId: empResponse?.companyId ?? existing.companyId,
				jobTitleId: empResponse?.jobTitleId ?? existing.jobTitleId,
				jobTitleName: empResponse?.jobTitleName ?? existing.jobTitleName,
				centerCostId: empResponse?.costCenterId ?? existing.centerCostId,
				costCenterName: empResponse?.costCenterName ?? existing.costCenterName,
				tradeUnionId: empResponse?.tradeUnionId ?? existing.tradeUnionId,
			} as WorkforcePlanTeamEmployeeModel;
		} catch (error) {
			console.error(error);
			this.employeeLoadError = true;
			this._toastService.showError("Não foi possível carregar os dados do colaborador.");
		} finally {
			this.employeeLoadingChange.emit(false);
		}
	}

	private async _loadTrainedEmployeesInJobTitleGroupId(): Promise<void> {
		if (!this.data?.workforcePlanId || !this.data?.employee?.jobTitleGroupId) return;

		try {
			this.isTrainedEmployeeLoading = true;
			const data = await this._drawerEmployeeService
				.getListTrainedEmployeesInJobTitleGroupId(
					this.data.workforcePlanId,
					this.data.employee.jobTitleGroupId
				)
				.toPromise();
			const filtered = this._filterTrainedEmployeesForOpenPosition(data || []);
			this.lists.trainedEmployeeOptions = filtered;

			if (!filtered.length) {
				this._toastService.showWarning(
					"Não foram encontrados colaboradores treinados para esse Cargo/Centro de Custo."
				);
				if (
					!this._openPositionHasJobTitle &&
					this.openPositionTarget?.costCenterId &&
					this.openPositionTarget?.jobTitleGroupId
				) {
					this._loadJobTitlesForOpenPosition(
						this.openPositionTarget.costCenterId,
						this.openPositionTarget.jobTitleGroupId
					);
				}
			} else if (!this._openPositionHasJobTitle) {
				this.lists.jobTitleOptions = this._buildTrainedJobTitleOptions(filtered);
			}

			this.isTrainedEmployeeLoading = false;
		} catch (error) {
			console.error(error);
			this.lists.trainedEmployeeOptions = [];
			this.isTrainedEmployeeLoading = false;
		}
	}

	private _loadCostCentersOptions(): void {
		if (!this.data?.companyId) return;

		this._costCenterService
			.listByCompanies([this.data.companyId])
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				this.lists.centerCostsOptions = response.data.map((item) => item.toOption());
			});
	}

	private _listJobTitleGroupsFromWorkforcePlan(costCenterId: string, companyId: string): void {
		this._workforceplanService
			.listJobTitleGroupsFromWorkforcePlan(companyId, costCenterId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(data) => {
					this.lists.jobTitleGroupOptions = data.data;
				},
				() => {
					this.lists.jobTitleGroupOptions = [];
				}
			);
	}

	private _listJobTitlesFromEffectiveWorkforcePlan(costCenterId: string, companyId: string): void {
		this._jobTitleService
			.listFromEffectiveWorkforcePlan(companyId, costCenterId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(data) => {
					this.lists.jobTitles = data.body.map((item) => JobTitleModel.create(item));
					this.lists.jobTitleOptions = data.body.map(
						(item) => ({ label: `${item.code} - ${item.title}`, value: item.id } as OptionModel)
					);
					this.isJobTitlesLoading = false;
				},
				() => {
					this.lists.jobTitles = [];
					this.lists.jobTitleOptions = [];
					this.isJobTitlesLoading = false;
				}
			);
	}

	private _loadJobTitlesForOpenPosition(costCenterId: string, jobTitleGroupId: string): void {
		if (!this.data?.companyId) return;

		this.isJobTitlesLoading = true;
		this._jobTitleService
			.listFromEffectiveWorkforcePlan(this.data.companyId, costCenterId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(response) => {
					const jobTitles = (response.body ?? [])
						.map((item) => JobTitleModel.create(item))
						.filter((item) => item.jobTitleGroupId === jobTitleGroupId);
					this._setJobTitleOptions(jobTitles);
					this.isJobTitlesLoading = false;
				},
				() => {
					this._loadJobTitlesByJobTitleGroup(jobTitleGroupId);
				}
			);
	}

	private _loadJobTitlesByJobTitleGroup(jobTitleGroupId: string): void {
		if (!this.data?.companyId) return;

		this.isJobTitlesLoading = true;
		this._jobTitleService
			.getJobTitlesByTitleAndGroup(this.data.companyId, jobTitleGroupId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(data) => {
					this._setJobTitleOptions(data);
					this.isJobTitlesLoading = false;
				},
				() => {
					this._toastService.showError("Não foi possível carregar as funções.");
					this.lists.jobTitleOptions = [];
					this.isJobTitlesLoading = false;
				}
			);
	}

	private _setJobTitleOptions(jobTitles: JobTitleModel[]): void {
		this.lists.jobTitles = jobTitles;
		this.lists.jobTitleOptions = jobTitles.map(
			(item) => ({ label: `${item.code} - ${item.title}`, value: item.id } as OptionModel)
		);
	}

	private _tradeUnionPayload(tradeUnionId: string | null | undefined): {
		tradeUnionId?: string | null;
		tradeUnionName?: string | null;
	} {
		if (this._companyHasTradeUnions === false) {
			if (!this._persistedTradeUnionId) return {};
			const payloadData = this.data?.solicitationDetails?.payloadData;
			return {
				tradeUnionId: this._persistedTradeUnionId,
				tradeUnionName: payloadData?.tradeUnionName ?? this._getTradeUnionTitleOption(this._persistedTradeUnionId),
			};
		}

		return {
			tradeUnionId: tradeUnionId ?? null,
			tradeUnionName: this._getTradeUnionTitleOption(tradeUnionId),
		};
	}

	private _applyEmployeeTradeUnionDefault(): void {
		if (this._persistedTradeUnionId || !this.canEditFieldToChangeJobTitleTrained) return;
		const tradeUnionCtrl = this.form.get("tradeUnionId");
		if (this._hasTradeUnionValue(tradeUnionCtrl?.value) || !this.data?.employee?.tradeUnionId) return;
		tradeUnionCtrl?.setValue(this.data.employee.tradeUnionId);
	}

	private _clearTradeUnionWhenCompanyHasNone(): void {
		const tradeUnionCtrl = this.form.get("tradeUnionId");
		tradeUnionCtrl?.clearValidators();
		if (!this._persistedTradeUnionId) {
			tradeUnionCtrl?.setValue(null, { emitEvent: false });
		}
		tradeUnionCtrl?.updateValueAndValidity({ emitEvent: false });
	}

	private _hasTradeUnionValue(value: unknown): boolean {
		return !!value && !(Array.isArray(value) && value.length === 0);
	}

	private _loadTradeUnionsOptions(): void {
		if (!this.data?.companyId) return;
		const params = {
			select: ["id", "code", "name"],
			filter: Filter.equalToGuid("companyId", this.data.companyId),
		} as QueryParams;
		this._domainService
			.getTradeUnions(params, true)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(response) => {
					this.lists.tradeUnionsOptions = response.data.map((i) => i.toOption());
					this._companyHasTradeUnions = this.lists.tradeUnionsOptions.length > 0;
					if (this._companyHasTradeUnions) {
						this._applyEmployeeTradeUnionDefault();
						return;
					}
					this._clearTradeUnionWhenCompanyHasNone();
				},
				() => {
					// Sem a lista de sindicatos, segue a mesma regra de empresa sem sindicato.
					this.lists.tradeUnionsOptions = [];
					this._companyHasTradeUnions = false;
					this._clearTradeUnionWhenCompanyHasNone();
				}
			);
	}

	private _loadMovementTypes(): void {
		this._movementTypeService
			.listMovementTypes()
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(res) => {
					this.lists.movementTypeOptions = res.data.map(
						(item) => ({ label: `${item.foreignId} - ${item.name}`, value: item.foreignId } as OptionModel)
					);
				},
				() => {
					this.lists.movementTypeOptions = [];
				}
			);
	}

	private _getJobTitleOption(jobTitleId: string): string {
		const opt = this.lists.jobTitleOptions?.find((item) => item.value === jobTitleId);
		if (!opt?.label) return "";
		return (opt.label as string).split(" - ")[1] || opt.label;
	}

	private _getCostCenterTitleOption(costCenterId: string): string {
		const opt = this.lists.centerCostsOptions?.find((item) => item.value === costCenterId);
		if (!opt?.label) return "";
		return (opt.label as string).split(" - ")[1] || opt.label;
	}

	private _getTradeUnionTitleOption(tradeUnionId: string | null | undefined): string | null {
		if (!tradeUnionId || !this.lists.tradeUnionsOptions?.length) return null;
		const opt = this.lists.tradeUnionsOptions.find((item) => item.value === tradeUnionId);
		return opt?.label ?? null;
	}
}
